"use client";

import { useEffect, useState, Suspense } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import {
  setSessionId,
  setStep,
  setVisitors,
  setCartItems,
  setLoading,
} from "@/store/cartSlice";
import { getSessionId } from "@/_helper/helper";
import { findByIDCartPaxDetails, findByIDCart } from "@/services/cart.services";
import CartStepper from "./CartStepper";
import Step2CartDetails from "./Step2CartDetails";
import Step3Payment from "./Step3Payment";
import CartLoader from "./CartLoader";
import { motion, AnimatePresence } from "motion/react";

function CartContent() {
  const searchParams = useSearchParams();
  const ticketId = searchParams.get("ticketId");
  const dispatch = useAppDispatch();
  const { currentStep, loading, sessionId } = useAppSelector(
    (state) => state.cart,
  );

  useEffect(() => {
    const sid = getSessionId();
    dispatch(setSessionId(sid));

    // If navigating from outside with a new ticket or hotel, always start from step 0
    const hotelId = searchParams.get("hotelId");
    if (ticketId || hotelId) {
      dispatch(setStep(0));
    }
  }, [dispatch, ticketId, searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 pt-12 pb-12">
      <CartLoader isLoading={loading} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <CartStepper currentStep={currentStep} />

        <div className="mt-8">
          <AnimatePresence mode="wait">
            {currentStep === 0 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Step2CartDetails
                  initialTicketId={ticketId ? parseInt(ticketId) : undefined}
                  initialHotelId={
                    searchParams.get("hotelId")
                      ? parseInt(searchParams.get("hotelId")!)
                      : undefined
                  }
                  initialRoomCategoryId={
                    searchParams.get("roomCategoryId")
                      ? parseInt(searchParams.get("roomCategoryId")!)
                      : undefined
                  }
                  initialMealPlanID={
                    searchParams.get("mealPlanID") ||
                    searchParams.get("mealPlan") ||
                    undefined
                  }
                />
              </motion.div>
            )}

            {currentStep === 1 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Step3Payment />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default function CartClient() {
  return (
    <Suspense fallback={<CartLoader isLoading={true} />}>
      <CartContent />
    </Suspense>
  );
}
